/**
 * Staff-side API helpers for recurring daycare series (migration 0096).
 * Kept local to the feature so `@/lib/api` (shared, heavily edited) stays
 * untouched; mirrors its `request()` conventions (credentials + JSON errors).
 */
import type {
  RecurringSeriesDto,
  SeriesCreateResult,
  SeriesInstanceDto,
  SeriesPreview,
  SeriesRuleBody,
} from '@/lib/recurrence';

export class RecurringApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly body: Record<string, unknown> = {},
  ) {
    super(`${status} ${code}`);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/recurring-series${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const code = typeof json.error === 'string' ? json.error : `http_${res.status}`;
    throw new RecurringApiError(code, res.status, json);
  }
  return res.json() as Promise<T>;
}

export const previewRecurringSeries = (body: SeriesRuleBody) =>
  call<SeriesPreview>('POST', '/preview', body);

export const createRecurringSeries = (body: SeriesRuleBody) =>
  call<SeriesCreateResult>('POST', '', body);

export const getRecurringSeries = (id: string) =>
  call<{ series: RecurringSeriesDto; instances: SeriesInstanceDto[] }>('GET', `/${id}`);

export const setRecurringSeriesStatus = (id: string, status: 'paused' | 'active') =>
  call<{ series: RecurringSeriesDto }>('PATCH', `/${id}`, { status });

export const cancelRecurringSeriesFuture = (id: string, fromDate?: string) =>
  call<{ series: RecurringSeriesDto; cancelledBookingIds: string[] }>('POST', `/${id}/cancel`, {
    scope: 'future',
    ...(fromDate ? { fromDate } : {}),
  });

export function errorMessage(err: unknown): string {
  if (err instanceof RecurringApiError) {
    const issues = err.body.issues;
    if (Array.isArray(issues) && issues.length > 0) return String(issues[0]);
    switch (err.code) {
      case 'no_daycare_service':
        return 'Add an active daycare service before creating a weekly series.';
      case 'vaccines_required':
        return 'This pet is missing a required vaccination.';
      case 'pet_not_found':
        return 'That pet does not belong to this customer.';
      default:
        return err.code.replace(/_/g, ' ');
    }
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}
